<template>
  <ModalDialog
    ref="dialog"
    title="Request Pricing / Stock Update"
    :submitting="submitting"
    submitTitle="Submit"
    icon="fa-money-check-edit-alt"
    @closed="onClosed"
    @submitted="onSubmitted">

    <FormGroup v-bind:form="dialogForm" :labelColumnSize="3">
      <Spinner v-if="loading" />
      <span class="text-danger" v-if="loadingError">{{loadingError}}</span>

      <template v-if="!loading && !loadingError">
        <FormRow label="Price Update Type">
          <LabelPriceRequestTypeBadge :type="PriceRequestTypes.supplier" />
        </FormRow>
        <FormRow label="Supplier"><FormStaticLabel>{{supplierData.name}}</FormStaticLabel></FormRow>
        <FormRow label="Update Reason" v-show="priceChangeList.length > 0"><FormInputTextArea placeholder="Update Reason" v-model="requestData.requestReason" field="requestReason" /></FormRow>

        <FormHorizontalRule />
        
        <div style="max-height: 40vh; overflow-y: scroll">
          <div v-if="priceChangeList.length > 0">
            <h3>Price Changes</h3>
            <StaticDataTable class="static-table-list" variant="table-sm">
              <template v-slot:header>
                <tr>
                  <th>Name</th>
                  <!-- Container List / PriceGroup List / ServiceRange List -->
                  <template v-for="(header, index) in priceHeaderList">
                    <th :key="index">{{header.name}}</th>
                  </template>
                </tr>
              </template>
              <template v-slot:body>
                <tr v-for="(priceChange, index) in priceChangeList" :key="index">
                  <td>{{priceChange.display}}</td>
                  <!-- Container List / PriceGroup List / ServiceRange List -->
                  <template v-for="(header, index) in priceHeaderList">
                    <td :key="index">
                      <span :class="[supplierPriceRemoved(priceChange, header.id, priceChangeKey) ? 'stock-price-removed' : '']" v-html="displaySupplierPriceChange(priceChange, header.id, priceChangeKey)" />
                    </td>
                  </template>
                </tr>
              </template>
            </StaticDataTable>
            <p class="text-center font-italic" v-if="priceChangeList.length == 0">No pricing updates found</p>
          </div>

          <div v-if="stockChangeList.length > 0">
            <h3>Alias Changes</h3>
            <StaticDataTable class="static-table-list" variant="table-sm">
              <template v-slot:header>
                <tr>
                  <th>Stock</th>
                  <th>Alias</th>
                </tr>
              </template>
              <template v-slot:body>
                <tr v-for="(stockChange, index) in stockChangeList" :key="index">
                  <td>{{stockChange.code}}</td>
                  <td>{{stockChange.alias}}</td>
                </tr>
              </template>
            </StaticDataTable>          
          </div>
          <br />
          <p class="text-muted font-italic" v-if="priceChangeList.length > 0">Note: Price updates will be sent for approval. Updates will not be active until approved</p>
          <p class="text-muted font-italic" v-if="stockChangeList.length > 0">Note: Alias changes are updated immediately</p>
        </div>
        
      </template>
    </FormGroup>
  </ModalDialog>
</template>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import ModalDialogMixin from '@/mixins/modalDialog'
import StaticDataTable from '@/components/table/staticDataTable'
import PriceRequestMixin from '@/modules/pricing/mixins/priceRequestMixin'
import LabelPriceRequestTypeBadge from '@/modules/pricing/components/labelPriceRequestTypeBadge'

import { InventoryStockTypes, StockPriceTypes, PriceRequestTypes } from '@/store/types'

export default {
  name: "SupplierPriceRequestUpdateDialog",
  mixins: [
    ModalDialogMixin,
    form,
    PriceRequestMixin
  ],
  components: {
    ModalDialog,
    StaticDataTable,
    LabelPriceRequestTypeBadge
  },
  props: {
    priceType: String,
    contactId: Number
  },
  data() {
    return {
      requestData: {},
      supplierData: {},

      supplierBulkPricingData: {},
      supplierProcessedPricingData: {},
      supplierServicePricingData: {},
      supplierGeneralPricingData: {},

      currentStockData: {},
      updateStockData: {},
      priceHeaderList: [],
      priceChangeList: [],
      stockChangeList: [],
      priceChangeKey: '',
      priceExtraKey: '',
      dialogForm: {},
      loading: false,
      loadingError: '',
      submitting: false
    }
  },
  created() {
    this.PriceRequestTypes = PriceRequestTypes
  },
  mounted() {
  },
  computed: {
  },
  methods: {
    show(stockData) {
      // Reset form
      this.requestData = {}
      this.supplierData = {}
      this.currentStockData = { }
      this.updateStockData = { }
      this.priceHeaderList = []
      this.priceChangeList = []
      this.stockChangeList = []
      this.dialogForm = { }
      this.submitting = false
      this.loading = false
      this.loadingError = ''

      // Stock Data HAS to exist
      if(stockData != null) {
        this.updateStockData = _.cloneDeep(stockData)

        // Get the current supplier pricing values
        this.loading = true

        Promise.all([
          this.$apiService.pricingService.getSupplierBulkPricingList(this.$props.contactId),
          this.$apiService.pricingService.getSupplierProcessedPricingList(this.$props.contactId),
          this.$apiService.pricingService.getSupplierServicePricingList(this.$props.contactId),
          this.$apiService.pricingService.getSupplierGeneralPricingList(this.$props.contactId),
          this.$apiService.contactService.getContact(this.$props.contactId)
        ]).then(results => {
          this.$data.supplierBulkPricingData = results[0].data
          this.$data.supplierProcessedPricingData = results[1].data
          this.$data.supplierServicePricingData = results[2].data
          this.$data.supplierGeneralPricingData = results[3].data

          this.$data.supplierData = results[4].data

          // Calculate the list of prices that have actually changed
          this.generatePriceChangeList()
        })
        .catch(err => {
          this.loadingError = 'Failed to load supplier / pricing data'
        })
        .finally(() => {
          this.$data.loading = false
        })

      } else {
        this.loadingError = 'No data provided'
      }

      return ModalDialogMixin.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(false)
    },
    onSubmitted() {
      this.submitting = true

      // Update records that have changed
      let dataUpdateList = this.generateUpdateData()
      let aliasUpdateList = this.generateAliasUpdateData()

      var updateData = {
        stockPriceList: dataUpdateList,
        stockAliasList: aliasUpdateList,
        requestReason: this.$data.requestData.requestReason
      }

      this.$apiService.pricingService.updateSupplierStockPrices(this.$data.supplierData.id, updateData)
      .then(response => {
        this.modalClosed(true)
        this.hide()
      })
      .catch(err => {
        this.$data.dialogForm = this.processFormResponse(err)
      })
      .finally(() => {
        this.$data.submitting = false
      })
    },

    // Calculate the list of prices that have actually changed
    generatePriceChangeList() {
      try {
        this.priceChangeList = []
        this.priceHeaderList = []
        this.stockChangeList = []

        // Glass - Bulk
        if(this.$props.priceType == StockPriceTypes.bulkContainer) {
          this.priceChangeKey = 'stockContainerId'

          this.priceHeaderList = this.$data.supplierBulkPricingData.stockContainerList.map(container => {
            return {
              id: container.id,
             stockContainerId: container.id,
              name: container.code
            }
          })

          this.addSupplierPriceChangeList(this.priceChangeList, this.stockChangeList, {
            currentStockPriceList: this.$data.supplierBulkPricingData.stockBulkPriceList,
            newStockPriceList: this.updateStockData.stockBulkPriceList,
            headerList: this.priceHeaderList,
            key: this.priceChangeKey,
          })
        }

        // Glass - Processed
        if(this.$props.priceType == StockPriceTypes.processedPriceGroup) {
          this.priceChangeKey = 'stockPriceGroupId'

          this.priceHeaderList = this.$data.supplierProcessedPricingData.priceGroupList.map(group => {
            return {
              id: group.id,
             stockPriceGroupId: group.id,
              name: group.name
            }
          })

          this.addSupplierPriceChangeList(this.priceChangeList, this.stockChangeList, {
            currentStockPriceList: this.$data.supplierProcessedPricingData.stockProcessedPriceList,
            newStockPriceList: this.updateStockData.stockProcessedPriceList,
            headerList: this.priceHeaderList,
            key: this.priceChangeKey,
          })
        }

        // Services
        if(this.$props.priceType == StockPriceTypes.servicePriceRange) {
          this.priceChangeKey = 'servicePriceRangeId'

          // Add in all possible ranges for headers
          this.priceHeaderList = []
          this.$data.supplierServicePricingData.servicePriceGroupList.forEach(priceGroup => {
            priceGroup.servicePriceRanges.forEach(priceRange => {
              if(!this.priceHeaderList.find(header => header.id == priceRange.id)) {
                this.priceHeaderList.push({
                  id: priceRange.id,
                 servicePriceRangeId: priceRange.id,
                  name: priceRange.name
                })
              }
            })
          })

          this.$data.supplierServicePricingData.servicePriceGroupList.forEach(priceGroup => {

            var existingStockList = _.cloneDeep(this.$data.supplierServicePricingData.stockServicePriceList)
            var updateStockList = _.cloneDeep(this.$data.updateStockData.stockServicePriceList)

            existingStockList.forEach(stock => {
              stock.prices = stock.prices.filter(stockPrice => stockPrice.stockPriceGroupId == priceGroup.id)
            })
            updateStockList.forEach(stock => {
              stock.prices = stock.prices.filter(stockPrice => stockPrice.stockPriceGroupId == priceGroup.id)
            })

            this.addSupplierPriceChangeList(this.priceChangeList, this.stockChangeList, {
              currentStockPriceList: existingStockList,
              newStockPriceList: updateStockList,
              headerList: this.priceHeaderList,
              key: this.priceChangeKey,
              displayCallback: (salesPrice, pricingType) => { return salesPrice.code + ' - ' + priceGroup.name }
            })
          })          
        }

        // General
        if(this.$props.priceType == StockPriceTypes.general) {
          this.priceChangeKey = null

          this.priceHeaderList = [{
            id: undefined,
            name: 'Price'
          }]

          this.addSupplierPriceChangeList(this.priceChangeList, this.stockChangeList, {
            currentStockPriceList: this.$data.supplierGeneralPricingData.stockSupplierPriceList,
            newStockPriceList: this.updateStockData.stockSupplierPriceList,
            headerList: this.priceHeaderList
          })
        }

      } catch(err) {
        console.error(err)
      }
    },    

    // Generate the update list sent to the server for updating
    generateUpdateData() {
      var updateList = []

      this.priceChangeList.forEach(stockInfo => {
        stockInfo.prices.forEach(priceInfo => {
          if(priceInfo.changed) {
            updateList.push({
             stockId: stockInfo.id,
             stockContainerId: priceInfo.stockContainerId,
             stockPriceGroupId: priceInfo.stockPriceGroupId,
             servicePriceRangeId: priceInfo.servicePriceRangeId,
              servicePricingType: priceInfo.servicePricingType,
              priceType: this.$props.priceType,
              isRemoved: priceInfo.isRemoved,
              price: priceInfo.price
            })
          }
        })
      })

      return updateList
    },

    // Generate the update list sent to the server for updating stock aliases
    generateAliasUpdateData() {
      return this.stockChangeList.map(aliasUpdate => {
        return {
         stockId: aliasUpdate.id,
          alias: aliasUpdate.alias
        }
      })
    }

  }
}
</script>